<template>
  <validation-observer ref="observer" v-slot="{ invalid }">
    <v-container class="px-0 pt-0">
      <v-card :class="localDisplayMode === DISPLAY_MODE.REGISTER ? 'dialog' : ''">
        <!-- 詳細モード時の編集と削除ボタン -->
        <div v-if="localDisplayMode === DISPLAY_MODE.DETAIL" class="absolute top-2 right-6">
          <v-btn elevation="0" icon @click="deleteConfirmDialog.show = true" class="pr-2">
            <v-icon size="30">mdi-delete</v-icon>
          </v-btn>
          <v-btn
            elevation="0"
            icon
            color="primary"
            @click="localDisplayMode = DISPLAY_MODE.UPDATE"
            class="pl-6"
          >
            <v-icon size="30">mdi-pencil</v-icon>
          </v-btn>
        </div>

        <!-- タイトル -->
        <div v-if="title">
          {{ title }}
        </div>

        <!-- コンテンツ -->
        <v-card-text>
          <div class="px-2">
            <!-- 薬名 -->
            <v-row class="pb-0">
              <v-col cols="12" class="pb-0">
                <label class="text-primary text-sm">
                  薬名<span v-if="!readonly" class="ml-1 red--text">*</span>
                </label>
                <validation-provider v-slot="{ errors }" name="薬名" rules="required">
                  <v-text-field
                    v-model="localMedication.name"
                    :readonly="readonly"
                    :outlined="!readonly"
                    dense
                    solo
                    flat
                    clear-icon="mdi-close-circle"
                    :error-messages="errors"
                  />
                </validation-provider>
              </v-col>
            </v-row>

            <!-- 時間帯・服用量 -->
            <v-row>
              <v-col cols="12" class="pt-0">
                <label class="text-primary text-sm"
                  >時間帯<span v-if="!readonly" class="ml-1 red--text">*</span
                  ><v-icon @click="openHelpDialog" class="bottom-[0.1rem]"
                    >mdi-help-circle-outline</v-icon
                  ></label
                >
                <validation-provider v-slot="{ errors }" name="時間帯" rules="required">
                  <v-btn-toggle
                    v-model="localMedication.times"
                    color="transparent"
                    multiple
                    class="w-full"
                  >
                    <v-btn
                      v-for="{ text, value } in MEDICATION_TIME_OPTIONS"
                      :key="value"
                      :value="value"
                      class="flex-1"
                      height="40"
                      :style="readonly ? { pointerEvents: 'none' } : {}"
                      :error-messages="errors"
                    >
                      <span class="text-gray-800">{{ text }}</span>
                      <v-icon
                        v-if="localMedication.times?.includes(value)"
                        color="teal"
                        class="!absolute"
                        size="30"
                      >
                        mdi-checkbox-blank-circle-outline
                      </v-icon>
                    </v-btn>
                  </v-btn-toggle>
                </validation-provider>
              </v-col>
              <v-row>
                <v-col cols="12" class="pl-6">
                  <label class="text-primary text-sm">服用量（一回量）</label>
                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        v-model="localMedication.dose.value"
                        type="number"
                        :readonly="readonly"
                        :outlined="!readonly"
                        dense
                        solo
                        flat
                        hide-details
                        min="1"
                      />
                    </v-col>
                    <v-col cols="6" class="d-flex align-center">
                      <template v-if="readonly">
                        <span class="text-sm">{{
                          DOSE_UNIT_LABELS[localMedication.dose.unit]
                        }}</span>
                      </template>
                      <template v-else>
                        <v-select
                          v-model="localMedication.dose.unit"
                          :items="DOSE_UNIT_OPTIONS"
                          :outlined="!readonly"
                          dense
                          hide-details
                        />
                      </template>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-row>

            <!-- 期間・頻度 -->
            <v-row>
              <v-col cols="12" class="pb-0">
                <label class="text-primary text-sm"
                  >期間<span v-if="!readonly" class="ml-1 red--text">*</span></label
                >
                <v-text-field
                  v-model="periodLabel"
                  readonly
                  :outlined="!readonly"
                  dense
                  solo
                  flat
                  @click="!readonly && (periodSettingDialog.show = true)"
                />
              </v-col>
              <v-row>
                <!-- 頻度 -->
                <v-col cols="12" class="pl-6 pt-0">
                  <label class="text-primary text-sm">
                    頻度<span v-if="!readonly" class="ml-1 red--text">*</span>
                  </label>
                  <v-row>
                    <v-col cols="6">
                      <v-text-field
                        v-model="frequencyText"
                        readonly
                        :outlined="!readonly"
                        dense
                        solo
                        flat
                        hide-details
                        @click="!readonly && (frequencySettingDialog.show = true)"
                      />
                    </v-col>
                    <v-col cols="6" class="d-flex align-center">
                      <span
                        v-if="localMedication.frequency.type === FREQUENCY_TYPE_KEYS.INTERVAL"
                        class="text-sm"
                        >日毎</span
                      >
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-row>

            <!-- メモ -->
            <v-row>
              <v-col cols="12">
                <label class="text-primary text-sm">メモ</label>
                <v-textarea
                  v-model="localMedication.memo"
                  :readonly="readonly"
                  :outlined="!readonly"
                  dense
                  solo
                  flat
                  auto-grow
                  rows="2"
                  hide-details
                  :placeholder="
                    localDisplayMode !== DISPLAY_MODE.DETAIL ? '例: 毎食前に服用する' : ''
                  "
                />
              </v-col>
            </v-row>

            <!-- ボタンエリア -->
            <div
              v-if="localDisplayMode === DISPLAY_MODE.UPDATE"
              class="flex justify-end space-x-4 mt-2"
            >
              <v-btn elevation="0" @click="cancel">キャンセル</v-btn>
              <v-btn elevation="0" color="primary" @click="save" :disabled="invalid">保存</v-btn>
            </div>
            <div
              v-else-if="localDisplayMode === DISPLAY_MODE.REGISTER"
              class="flex justify-end space-x-4 mt-4"
            >
              <v-btn elevation="0" @click="cancel">キャンセル</v-btn>
              <v-btn elevation="0" color="primary" @click="save" :disabled="invalid">保存</v-btn>
            </div>
          </div>
        </v-card-text>

        <!-- 期間設定ダイアログ -->
        <v-dialog
          v-model="periodSettingDialog.show"
          :max-width="dialogMaxWidth"
          persistent
          no-click-animation
        >
          <PeriodSetting
            :key="periodSettingDialog.updateKey"
            :period="localMedication.period"
            @set="setPeriod"
            @close="cancelPeriod"
          />
        </v-dialog>

        <!-- 頻度設定ダイアログ -->
        <v-dialog
          v-model="frequencySettingDialog.show"
          :max-width="dialogMaxWidth"
          persistent
          no-click-animation
        >
          <FrequencySetting
            :key="frequencySettingDialog.updateKey"
            :frequency="localMedication.frequency"
            @set="setFrequency"
            @close="cancelFrequency"
          />
        </v-dialog>

        <!-- 削除確認ダイアログ -->
        <v-dialog v-model="deleteConfirmDialog.show" :max-width="confirmDialogMaxWidth">
          <confirm-dialog
            v-if="deleteConfirmDialog.show"
            :title="`薬名（${localMedication.name}）の服薬情報を削除します。\nよろしいですか？`"
            okButtonColor="error"
            @ok="showFinalDeleteConfirmDialog"
            @cancel="deleteConfirmDialog.show = false"
          ></confirm-dialog>
        </v-dialog>

        <!-- 最終削除確認ダイアログ -->
        <v-dialog v-model="finalDeleteConfirmDialog.show" :max-width="confirmDialogMaxWidth">
          <confirm-dialog
            title="この操作を実行すると、元に戻すことはできません。本当に削除してもよろしいですか？ "
            okButtonColor="error"
            @ok="deleteMedication(medication.medicationId)"
            @cancel="finalDeleteConfirmDialog.show = false"
          ></confirm-dialog>
        </v-dialog>
        <!-- ヘルプダイアログ -->
        <v-dialog v-model="helpDialog.show" max-width="500px">
          <help-dialog
            :title="helpDialogTitle"
            :description="helpDialogDescription"
            @close="closeHelpDialog"
          />
        </v-dialog>
      </v-card>
    </v-container>
  </validation-observer>
</template>

<script>
import Confirm from "@/components/dialogs/Confirm";
import Help from "@/components/dialogs/Help";
import FrequencySetting from "@/components/dialogs/MedicationFrequencySetting";
import PeriodSetting from "@/components/dialogs/MedicationPeriodSetting";
import {
  DISPLAY_MODE,
  DOSE_UNIT_LABELS,
  DOSE_UNIT_OPTIONS,
  FREQUENCY_TYPE_KEYS,
  FREQUENCY_TYPE_LABELS,
  MEDICATION_TIME_OPTIONS,
} from "@/const/const";
import errorHandlerMixin from "@/mixins/errorHandlerMixin";
import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  Timestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import moment from "moment";
import { mapActions, mapState } from "vuex";

export default {
  name: "Medication",
  components: {
    // 期間設定ダイアログ
    PeriodSetting,
    // 頻度設定ダイアログ
    FrequencySetting,
    ConfirmDialog: Confirm,
    HelpDialog: Help,
  },
  mixins: [errorHandlerMixin()],
  props: {
    // 表示モード
    displayMode: {
      required: true,
      type: Number,
    },
    // タイトル（ダイアログで利用する場合）
    title: {
      required: false,
      type: String,
      default: "",
    },
    // 服薬情報
    medication: {
      required: false,
      type: Object,
      default: () => ({
        name: "",
        times: [],
        dose: {
          value: null,
          unit: "",
        },
        period: {
          from: Timestamp.fromDate(new Date()),
          to: null,
        },
        frequency: {
          type: "daily",
        },
        memo: "",
      }),
    },
    // 患者UID
    patientUid: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      // 表示モード
      DISPLAY_MODE,
      localDisplayMode: this.displayMode,
      // 服薬情報
      localMedication: {},
      // 時間帯
      MEDICATION_TIME_OPTIONS,
      // 服用量単位
      DOSE_UNIT_LABELS,
      DOSE_UNIT_OPTIONS,
      // 頻度タイプ
      FREQUENCY_TYPE_KEYS,
      FREQUENCY_TYPE_LABELS,
      // 期間設定ダイアログ
      periodSettingDialog: { show: false, updateKey: 1 },
      // 頻度設定ダイアログ
      frequencySettingDialog: { show: false, updateKey: 1 },
      // 削除確認ダイアログ
      deleteConfirmDialog: {
        show: false,
      },
      // 最終削除確認ダイアログ
      finalDeleteConfirmDialog: {
        show: false,
      },
      // ヘルプダイアログ
      helpDialog: {
        show: false,
      },
      helpDialogTitle: "服薬時間帯",
      helpDialogDescription: `服薬時間は個人の生活リズムに合わせて調整してください。例えば、「起」は起床後、「朝」は朝食前後を指します。あくまで目安として、必ず医師の指示に従ってください。`,
    };
  },
  computed: {
    ...mapState("user", ["selfUser"]),
    patient() {
      return this.$store.state.user.selfUser;
    },
    readonly() {
      return this.localDisplayMode == DISPLAY_MODE.DETAIL;
    },
    // 期間
    periodLabel() {
      // from または from と to が設定されている場合の表示
      if (this.localMedication.period?.from && this.localMedication.period?.to) {
        const fromFormatted = moment(this.localMedication.period.from.toDate()).format(
          "YYYY年M月D日"
        );
        const toFormatted = moment(this.localMedication.period.to.toDate()).format("YYYY年M月D日");
        return `${fromFormatted} ~ ${toFormatted}`;
      }

      // from のみが設定されている場合
      if (this.localMedication.period.from) {
        const fromFormatted = moment(this.localMedication.period.from.toDate()).format(
          "YYYY年M月D日"
        );
        return `${fromFormatted} ~`;
      }

      return "";
    },
    // 頻度
    frequencyText() {
      // X 日毎の場合は、間隔（日数）を表示
      if (this.localMedication?.frequency.type == FREQUENCY_TYPE_KEYS.INTERVAL) {
        return this.localMedication?.frequency.intervalDays;
      }

      return FREQUENCY_TYPE_LABELS[this.localMedication?.frequency.type];
    },
    dialogMaxWidth() {
      if (window.innerWidth >= 1024) {
        return "350px";
      } else {
        return "90vw";
      }
    },
    confirmDialogMaxWidth() {
      if (window.innerWidth >= 1024) {
        return "480px";
      } else {
        return "90vw";
      }
    },
  },
  methods: {
    ...mapActions("api", ["incrementRunningApiCount", "decrementRunningApiCount"]),
    // 初期化
    initializeLocalMedication() {
      this.localMedication = {
        name: this.medication.name,
        times: [...this.medication.times],
        dose: { ...this.medication.dose },
        period: { ...this.medication.period },
        frequency: { ...this.medication.frequency },
        memo: this.medication.memo,
        medicationId: this.medication.medicationId,
      };
    },
    // キャンセル
    cancel() {
      this.periodSettingDialog.updateKey++;
      this.frequencySettingDialog.updateKey++;
      // 編集モード
      if (this.localDisplayMode == DISPLAY_MODE.UPDATE) {
        // 初期化して詳細モードに切り替える
        this.initializeLocalMedication();
        this.localDisplayMode = DISPLAY_MODE.DETAIL;
      } else if (this.localDisplayMode == DISPLAY_MODE.REGISTER) {
        // 登録モード（ダイアログ）
        this.$emit("close");
      }
    },
    // 保存
    async save() {
      try {
        this.incrementRunningApiCount();

        const db = this.$firestore;

        const recordRef = doc(db, "users", this.patientUid);

        // 患者情報
        const {
          hospitalIds,
          lastName,
          firstName,
          lastNameKana,
          firstNameKana,
          gender,
          dateOfBirth,
          postCode,
          prefectureCode,
          municipality,
          addressNumber,
          buildingNameAndRoomNumber,
          phoneNumber,
          doctorUids,
          diseases,
          medications,
          note,
        } = this.patient;

        let updatedMedications = [...(medications ?? [])];
        let previousMedication = null;

        if (this.localMedication.medicationId) {
          // 更新前のデータを取得
          previousMedication = updatedMedications.find(
            (med) => med.medicationId === this.localMedication.medicationId
          );
          // 更新
          updatedMedications = updatedMedications.map((medication) =>
            medication.medicationId === this.localMedication.medicationId
              ? this.localMedication
              : medication
          );
        } else {
          // 登録
          this.localMedication.medicationId = doc(collection(db, "temp")).id;
          updatedMedications.push(this.localMedication);
        }
        // 患者情報を更新
        await updateDoc(recordRef, {
          hospitalIds,
          lastName,
          firstName,
          lastNameKana,
          doctorUids,
          firstNameKana,
          gender,
          dateOfBirth,
          postCode,
          prefectureCode,
          municipality,
          addressNumber,
          buildingNameAndRoomNumber,
          phoneNumber,
          diseases,
          medications: updatedMedications,
          note,
          updatedUserId: this.selfUser.uid,
          updated: serverTimestamp(),
        });

        // 更新時、recordsを更新
        if (previousMedication) {
          await this.updateRecords(this.localMedication, previousMedication);
        }

        // 登録時、recordsを更新
        await this.updateRecords(this.localMedication);

        this.decrementRunningApiCount();

        this.$store.dispatch("snackbar/openSnackbar", {
          text: `服薬情報を保存しました`,
          color: "success",
        });
        this.localDisplayMode = DISPLAY_MODE.DETAIL;
        this.$emit("close");
      } catch (error) {
        this.showError(`服薬情報の保存に失敗しました`);
      }
    },
    // 最終削除確認ダイアログを表示
    showFinalDeleteConfirmDialog() {
      this.deleteConfirmDialog.show = false;
      this.finalDeleteConfirmDialog.show = true;
    },
    // 削除
    async deleteMedication(medicationId) {
      try {
        this.incrementRunningApiCount();
        const db = this.$firestore;
        const recordRef = doc(db, "users", this.patientUid);

        const targetMedication = this.patient.medications.find(
          (med) => med.medicationId === medicationId
        );

        // records から medicationChecks の削除処理
        await this.deleteMedicationFromRecords(targetMedication);

        // 指定したIDの薬を除外
        const updatedMedications = this.patient.medications.filter(
          (medication) => medication.medicationId !== medicationId
        );

        // データ更新
        await updateDoc(recordRef, {
          medications: updatedMedications,
          updatedUserId: this.selfUser.uid,
          updated: serverTimestamp(),
        });

        this.decrementRunningApiCount();
        this.$store.dispatch("snackbar/openSnackbar", {
          text: `服薬情報を削除しました`,
          color: "success",
        });
      } catch (error) {
        this.showError("服薬情報の削除に失敗しました");
      }
    },
    // recordsを更新
    async updateRecords(medication, previousMedication) {
      try {
        const db = this.$firestore;
        const recordCollection = collection(db, `users/${this.patientUid}/records`);

        const fromDate = medication.period.from.toDate();
        let toDatesub = null;
        if (!medication.period.to) {
          const toDate = new Date(fromDate);
          toDate.setDate(toDate.getDate() + 99);
          toDatesub = Timestamp.fromDate(toDate).toDate();
        } else {
          toDatesub = medication.period.to.toDate();
        }
        const toDate = toDatesub;
        const intervalDays =
          medication.frequency?.type === "interval" ? medication.frequency.intervalDays : 1;

        const batch = writeBatch(db);
        const batchUpdates = [];

        // 更新の場合
        if (previousMedication) {
          const prevFromDate = previousMedication?.period?.from?.toDate();
          let PrevToDatesub = null;
          if (!previousMedication.period.to) {
            const toDate = new Date(fromDate);
            toDate.setDate(toDate.getDate() + 99);
            PrevToDatesub = Timestamp.fromDate(toDate).toDate();
          } else {
            PrevToDatesub = previousMedication.period.to.toDate();
          }
          const prevToDate = PrevToDatesub;
          const prevIntervalDays =
            previousMedication.frequency?.type === "interval"
              ? previousMedication.frequency.intervalDays
              : 1;
          if (prevFromDate && prevToDate) {
            for (
              let date = new Date(prevFromDate);
              date <= prevToDate;
              date.setDate(date.getDate() + prevIntervalDays)
            ) {
              const yyyymm =
                date.getFullYear().toString() + String(date.getMonth() + 1).padStart(2, "0");
              const dd = String(date.getDate()).padStart(2, "0");
              const recordRef = doc(recordCollection, yyyymm);
              const recordSnap = await getDoc(recordRef);
              if (!recordSnap.exists()) continue;

              let recordData = recordSnap.data();
              if (!recordData[dd]) continue;

              let medicationChecks = recordData[dd].medicationChecks || [];
              medicationChecks = medicationChecks.filter(
                (mc) => mc.medicationId !== previousMedication.medicationId
              );
              recordData[dd].medicationChecks = medicationChecks.length > 0 ? medicationChecks : [];

              batchUpdates.push({ ref: recordRef, dd, data: { medicationChecks } });
            }
          }
        }

        for (
          let date = new Date(fromDate);
          date <= toDate;
          date.setDate(date.getDate() + intervalDays)
        ) {
          const yyyymm =
            date.getFullYear().toString() + String(date.getMonth() + 1).padStart(2, "0");
          const dd = String(date.getDate()).padStart(2, "0");

          const recordRef = doc(recordCollection, yyyymm);
          const recordSnap = await getDoc(recordRef);
          let recordData = recordSnap.exists() ? recordSnap.data() : {};

          let dayRecord = recordData[dd] || {};
          let medicationChecks = dayRecord.medicationChecks || [];

          const index = medicationChecks.findIndex(
            (mc) => mc.medicationId === medication.medicationId
          );
          if (index !== -1) {
            medicationChecks[index] = {
              ...medicationChecks[index],
              medicationId: medication.medicationId,
              medicationName: medication.name,
              scheduleTimes: medication.times,
            };
          } else {
            medicationChecks.push({
              medicationId: medication.medicationId,
              medicationName: medication.name,
              scheduleTimes: medication.times,
              checktimes: [],
            });
          }
          batchUpdates.push({ ref: recordRef, dd, data: { medicationChecks } });
        }

        batchUpdates.forEach(({ ref, dd, data }) => {
          batch.set(ref, { [dd]: data }, { merge: true });
        });
        await batch.commit();
      } catch (error) {
        this.showError("服薬情報（レコード）の更新に失敗しました");
      }
    },

    async deleteMedicationFromRecords(medication) {
      try {
        const db = this.$firestore;
        const recordCollection = collection(db, `users/${this.patientUid}/records`);
        const fromDate = medication.period.from.toDate();
        let toDatesub = null;
        if (!medication.period.to) {
          const toDate = new Date(fromDate);
          toDate.setDate(toDate.getDate() + 99);
          toDatesub = Timestamp.fromDate(toDate).toDate();
        } else {
          toDatesub = medication.period.to.toDate();
        }

        const toDate = toDatesub;
        const intervalDays =
          medication.frequency?.type === "interval" ? medication.frequency.intervalDays : 1;
        const batch = writeBatch(db);
        const batchUpdates = [];

        for (
          let date = new Date(fromDate);
          date <= toDate;
          date.setDate(date.getDate() + intervalDays)
        ) {
          const yyyymm =
            date.getFullYear().toString() + String(date.getMonth() + 1).padStart(2, "0");
          const dd = String(date.getDate()).padStart(2, "0");

          const recordRef = doc(recordCollection, yyyymm);
          const recordSnap = await getDoc(recordRef);
          if (!recordSnap.exists()) continue;

          let recordData = recordSnap.data();
          if (!recordData[dd]) continue;
          let medicationChecks = recordData[dd].medicationChecks || [];
          medicationChecks = medicationChecks.filter(
            (mc) => mc.medicationId !== medication.medicationId
          );

          batchUpdates.push({ ref: recordRef, dd, data: { medicationChecks } });
        }
        batchUpdates.forEach(({ ref, dd, data }) => {
          batch.set(ref, { [dd]: data }, { merge: true });
        });
        await batch.commit();
      } catch (error) {
        this.showError("服薬情報（レコード）の削除に失敗しました");
      }
    },

    // ====================
    // 期間設定ダイアログ
    // ====================
    // キャンセル
    cancelPeriod() {
      this.periodSettingDialog.show = false;
    },
    // 設定
    setPeriod(period) {
      this.localMedication.period = { ...period };
      this.periodSettingDialog.show = false;
    },
    // ====================
    // 頻度設定ダイアログ
    // ====================
    // キャンセル
    cancelFrequency() {
      this.frequencySettingDialog.show = false;
    },
    // 設定
    setFrequency(frequency) {
      this.localMedication.frequency = { ...frequency };
      this.frequencySettingDialog.show = false;
    },
    // ヘルプダイアログ閉じる
    closeHelpDialog() {
      this.helpDialog.show = false;
    },
    // ヘルプダイアログ開く
    openHelpDialog() {
      this.helpDialog.show = true;
    },
  },
  created() {
    this.initializeLocalMedication();
  },
};
</script>

<style scoped>
/* ダイアログは、境界線を非表示 */
.v-card.dialog {
  box-shadow: none !important;
  border: none;
}
</style>
